/**
 * The do-not-scan list: websites whose owner asked WebAdmin to stop checking
 * them, and the form to add one. An "Exclusions (N)" button on the Skannid tab
 * and the side panel it opens.
 *
 * It lives on Skannid (it sat above the call queue until plan step 3) because
 * it overrules the scan autopilot and the pasted list the bulk button starts,
 * and those are the two ways a stranger's site lands in the scans table. It
 * overrules the brain, the lead watch and the AI alert calls as well, which the
 * panel says. Until plan step 5 it was a card between the autopilot and the
 * table; it is a button now because it is opened rarely - when an owner writes
 * in, or when a scan is refused - and a card of its own cost every visit a
 * row of the tab.
 *
 * THE PANEL IS OPENED FROM THREE PLACES, which is why its open state belongs
 * to the caller: the button, the line a refused scan prints ("this owner asked
 * us not to check the site") and the notice after a bulk scan that left sites
 * out. Each of those is somebody asking "which list?", and the answer is one
 * click away instead of a scroll and a toggle.
 *
 * Taking a site off asks first, because it lets every engine back onto a site
 * whose owner said no. The list and the form are `manage:ariregister` to
 * change and readable by everyone who can open the tab.
 *
 * It has nothing to do with Saidiseire: a site on this list is a stranger's
 * opt-out from prospecting, and it never pauses monitoring a client pays for.
 *
 * State lives in `useScanExclusions.ts`; the API mirror in `lib/scanExclusions.ts`.
 */
import { useId, useState } from "react";
import { Loader2, ShieldOff, Trash2 } from "lucide-react";
import {
  Button,
  ConfirmDialog,
  Input,
  Label,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  cn,
} from "@wa/ui";
import { useI18n } from "../../../../i18n/I18nProvider";
import { formatWhen } from "../../../../lib/siteScan";
import { dateLocale } from "../../../../lib/siteWatch";
import { EXCLUSION_REASON_MAX, type ScanExclusion } from "../../../../lib/scanExclusions";
import { Btn, fmtNum } from "../../../ui";
import type { T } from "../../site-scanner/types";
import { useScanExclusions, type ExclusionNotice } from "./useScanExclusions";

function noticeText(notice: ExclusionNotice, t: T): string {
  if (notice.kind === "error") return t(`leads.exclusions.error.${notice.key}`);
  return t(`leads.exclusions.${notice.kind}`, { domain: notice.domain });
}

function Row({
  item,
  canManage,
  busy,
  dates,
  onRemove,
  t,
}: {
  item: ScanExclusion;
  canManage: boolean;
  busy: boolean;
  dates: string;
  onRemove: () => void;
  t: T;
}) {
  const when = formatWhen(item.createdAt, dates);
  return (
    <li className="flex items-start gap-3 border-t border-[var(--crm-hair)] px-[26px] py-3">
      <div className="min-w-0 flex-1">
        <div className="crm-trunc text-[13px] font-[550]">{item.domain}</div>
        <div className={cn("crm-sub text-[12.5px] leading-snug", !item.reason && "italic")}>
          {item.reason ?? t("leads.exclusions.noReason")}
        </div>
        <div className="crm-mut crm-tnum text-[11.5px]">
          {item.addedByName
            ? t("leads.exclusions.addedBy", { who: item.addedByName, when })
            : t("leads.exclusions.addedAt", { when })}
        </div>
      </div>
      {canManage ? (
        <Button variant="ghost" size="sm" disabled={busy} onClick={onRemove} aria-label={`${t("leads.exclusions.remove")} ${item.domain}`}>
          <Trash2 />
          {t("leads.exclusions.remove")}
        </Button>
      ) : null}
    </li>
  );
}

export function ExclusionSheet({
  open,
  onOpenChange,
  canManage,
  t,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canManage: boolean;
  t: T;
}) {
  const { locale } = useI18n();
  const dates = dateLocale(locale);
  const list = useScanExclusions();
  const [domain, setDomain] = useState("");
  const [reason, setReason] = useState("");
  const [confirm, setConfirm] = useState<string | null>(null);
  const domainId = useId();
  const reasonId = useId();

  const items = list.items ?? [];
  const summary = list.items
    ? items.length
      ? t("leads.exclusions.summary", { n: items.length })
      : t("leads.exclusions.empty")
    : list.loadFailed
      ? t("leads.exclusions.loadFailed")
      : t("common.loading");

  const submit = async () => {
    if (!domain.trim() || list.busy) return;
    if (await list.add(domain, reason)) {
      setDomain("");
      setReason("");
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        {/* The count rides in the button, so the list says it exists - and how
            long it is - without taking a row of the tab. No count until the
            list has loaded: a "0" that is really "not asked yet" is a lie. The
            button is the sheet's trigger, so closing the panel puts focus back
            on it, whichever of the three ways it was opened. */}
        <SheetTrigger asChild>
          <Btn small title={t("leads.exclusions.title")}>
            <ShieldOff className="size-4" />
            {t("leads.exclusions.button")}
            {list.items ? <span className="crm-mut crm-tnum">{fmtNum(items.length)}</span> : null}
          </Btn>
        </SheetTrigger>

        <SheetContent className="sm:max-w-2xl" closeLabel={t("common.close")}>
          <SheetHeader className="px-[26px] pr-14">
            <SheetTitle>{t("leads.exclusions.title")}</SheetTitle>
            <SheetDescription>{summary}</SheetDescription>
          </SheetHeader>

          {/* No side padding of its own: the rows keep the 26px gutter they
              had in the card, and their hairlines run edge to edge. */}
          <SheetBody className="px-0 pt-1">
            <p className="crm-sub px-[26px] pt-3 text-[12px] leading-snug">{t("leads.exclusions.scope")}</p>

            {list.loadFailed ? (
              <div className="px-[26px] pt-3">
                <Button variant="outline" size="sm" onClick={() => void list.reload()}>
                  {t("leads.exclusions.retry")}
                </Button>
              </div>
            ) : null}

            {canManage ? (
              <form
                className="grid gap-3 px-[26px] pb-4 pt-3.5 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)_auto] sm:items-end"
                onSubmit={(e) => {
                  e.preventDefault();
                  void submit();
                }}
              >
                <div className="min-w-0">
                  <Label htmlFor={domainId} className="mb-1.5 block text-[12px]">
                    {t("leads.exclusions.domain")}
                  </Label>
                  <Input
                    id={domainId}
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                    placeholder={t("leads.exclusions.domainPlaceholder")}
                    autoComplete="off"
                    spellCheck={false}
                    maxLength={500}
                    disabled={list.busy}
                  />
                </div>
                <div className="min-w-0">
                  <Label htmlFor={reasonId} className="mb-1.5 block text-[12px]">
                    {t("leads.exclusions.reason")}
                  </Label>
                  <Input
                    id={reasonId}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder={t("leads.exclusions.reasonPlaceholder")}
                    maxLength={EXCLUSION_REASON_MAX}
                    disabled={list.busy}
                  />
                </div>
                <Button type="submit" disabled={list.busy || !domain.trim()}>
                  {list.busy ? <Loader2 className="animate-spin" /> : null}
                  {t(list.busy ? "leads.exclusions.adding" : "leads.exclusions.add")}
                </Button>
              </form>
            ) : null}

            {list.notice ? (
              <p
                className={cn("crm-sub px-[26px] pb-3", list.notice.kind === "error" && "text-[var(--color-destructive)]")}
                role="status"
              >
                {noticeText(list.notice, t)}
              </p>
            ) : null}

            {items.length ? (
              <ul className="pb-1">
                {items.map((item) => (
                  <Row
                    key={item.domain}
                    item={item}
                    canManage={canManage}
                    busy={list.busy}
                    dates={dates}
                    onRemove={() => setConfirm(item.domain)}
                    t={t}
                  />
                ))}
              </ul>
            ) : null}
          </SheetBody>
        </SheetContent>
      </Sheet>

      {/* Outside the sheet and above it: the kit's dialog stacks over the
          panel (z-70 over z-50), and Radix keeps Escape and outside clicks
          for the top one, so cancelling here does not close the list too. */}
      <ConfirmDialog
        open={confirm !== null}
        title={t("leads.exclusions.removeTitle", { domain: confirm ?? "" })}
        description={t("leads.exclusions.removeBody")}
        confirmLabel={t("leads.exclusions.remove")}
        cancelLabel={t("leads.exclusions.cancel")}
        loading={list.busy}
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          const target = confirm;
          setConfirm(null);
          if (target) void list.remove(target);
        }}
      />
    </>
  );
}
